import type {
  SpoolWithRelations,
  CreateSpoolInput,
  UpdateSpoolInput,
  MoveSpoolInput,
} from '@maker-toolchest/shared';
import { api } from '@/lib/api';

// ============================================================================
// SPOOLS API
// ============================================================================

export interface SpoolListResponse {
  data: SpoolWithRelations[];
  meta?: {
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface SpoolQueryParams {
  page?: number;
  limit?: number;
  tenantFilamentId?: string;
  status?: 'IN_STORAGE' | 'LOADED' | 'IN_USE' | 'EMPTY' | 'DISCARDED';
  locationType?: 'PRINTER' | 'STORAGE' | 'NONE';
  printerId?: string;
  storageLocationId?: string;
  sortBy?: 'createdAt' | 'remainingWeight' | 'purchaseDate' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export const spoolApi = {
  /**
   * List all spools with filters
   */
  list: async (params?: SpoolQueryParams): Promise<SpoolListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.tenantFilamentId) searchParams.set('tenantFilamentId', params.tenantFilamentId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.locationType) searchParams.set('locationType', params.locationType);
    if (params?.printerId) searchParams.set('printerId', params.printerId);
    if (params?.storageLocationId) searchParams.set('storageLocationId', params.storageLocationId);
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);

    const query = searchParams.toString();
    const response = await api.get<SpoolWithRelations[]>(`/spools${query ? `?${query}` : ''}`);
    return response as unknown as SpoolListResponse;
  },

  /**
   * Get a single spool by ID
   */
  get: async (id: string): Promise<SpoolWithRelations> => {
    const response = await api.get<SpoolWithRelations>(`/spools/${id}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch spool');
    }
    return response.data;
  },

  /**
   * Create a new spool
   */
  create: async (data: CreateSpoolInput): Promise<SpoolWithRelations> => {
    const response = await api.post<SpoolWithRelations>('/spools', data);
    if (!response.success || !response.data) {
      throw new Error('Failed to create spool');
    }
    return response.data;
  },

  /**
   * Update a spool
   */
  update: async (id: string, data: UpdateSpoolInput): Promise<SpoolWithRelations> => {
    const response = await api.patch<SpoolWithRelations>(`/spools/${id}`, data);
    if (!response.success || !response.data) {
      throw new Error('Failed to update spool');
    }
    return response.data;
  },

  /**
   * Delete a spool
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/spools/${id}`);
  },

  /**
   * Move a spool to a new location
   */
  move: async (id: string, data: MoveSpoolInput): Promise<SpoolWithRelations> => {
    const response = await api.post<SpoolWithRelations>(`/spools/${id}/move`, data);
    if (!response.success || !response.data) {
      throw new Error('Failed to move spool');
    }
    return response.data;
  },

  /**
   * Update spool weight
   */
  updateWeight: async (id: string, remainingWeight: number): Promise<SpoolWithRelations> => {
    const response = await api.patch<SpoolWithRelations>(`/spools/${id}/weight`, { remainingWeight });
    if (!response.success || !response.data) {
      throw new Error('Failed to update spool weight');
    }
    return response.data;
  },
};
